'use client';

import { useCallback, useEffect, useState } from 'react';
import { readSplitVideoPreference, writeSplitVideoPreference } from '@/lib/cop-stage';

/**
 * "Video beside the map" outside a manual session, remembered between sessions.
 *
 * Read AFTER mount, never during render, for the reason `useManualStageLayout`
 * gives: the server renders without any `localStorage`, so seeding state from it
 * during the first render is a hydration mismatch. The first paint is therefore
 * always the map alone, and a stored preference replaces it on the next tick.
 *
 * Unlike the manual layout hook there is no `onBeforeChange`: outside a session
 * there is no drag on the wire to cancel before the stage is re-hosted.
 */
export function useSplitVideoPreference({
  operator = null,
}: {
  /** Stable operator identity (email today). A shared console must not blend two operators' choices. */
  operator?: string | null;
} = {}): {
  on: boolean;
  setOn: (next: boolean) => void;
  /** False when the choice could not be persisted, so the caller can say so rather than lie. */
  persisted: boolean;
} {
  const [on, setOnState] = useState(false);
  const [persisted, setPersisted] = useState(true);

  useEffect(() => {
    setOnState(readSplitVideoPreference(operator));
  }, [operator]);

  const setOn = useCallback(
    (next: boolean) => {
      setPersisted(writeSplitVideoPreference(next, operator));
      setOnState(next);
    },
    [operator],
  );

  return { on, setOn, persisted };
}
